import React, { useState, useEffect, useCallback } from 'react';
import { getOrganizationLeadsPaginated } from '../../api';
import CustomerDetail from '../CustomerDetail';
import DashboardNav from '../../components/dashboard/DashboardNav';
import LeadsFilter from '../../components/common/LeadsFilter';
import { useNavigate, useParams } from 'react-router-dom';

function OrganizationLeads({ user, organizationId, organizationName, onBack }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [availableTeams, setAvailableTeams] = useState([]);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [availableServiceProviders, setAvailableServiceProviders] = useState([]);
  const [availableAgenturen, setAvailableAgenturen] = useState([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [activeSection] = useState('organization-leads');
  const navigate = useNavigate();
  const { customerId } = useParams();

  // Pagination State
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 50,
    hasNextPage: false,
    hasPrevPage: false
  });

  const [filters, setFilters] = useState({
    search: '',
    agenturId: '',
    projekt: '',
    sortBy: 'erstellt',
    sortOrder: 'desc',
    dateFrom: '',
    dateTo: '',
    statusPv: '',
    statusWp: '',
    dienstleister: ''
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth > 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Verwende organizationId aus Props
  const currentOrganizationId = organizationId;

  // Responsive Styles (wie in Leads.js)
  const styles = {
    container: {
      margin: '0 auto',
      maxWidth: '1400px',
      padding: isMobile ? '5px' : '15px',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: isMobile ? 'flex-start' : 'center',
      marginBottom: '15px',
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '10px' : '0',
    },
    title: {
      color: '#2d3748',
      fontSize: isMobile ? '1.2rem' : '1.6rem',
      margin: 0,
    },
    filters: {
      background: 'white',
      borderRadius: '6px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      padding: isMobile ? '10px' : '15px',
      marginBottom: '15px',
    },
    filterRow: {
      display: 'flex',
      gap: isMobile ? '8px' : '15px',
      marginBottom: '12px',
      flexWrap: 'wrap',
      flexDirection: isMobile ? 'column' : 'row',
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: isMobile ? 'auto' : '120px',
      flex: isMobile ? '1' : 'none',
    },
    filterLabel: {
      fontSize: '0.8rem',
      fontWeight: '500',
      color: '#4a5568',
      marginBottom: '4px',
    },
    filterInput: {
      padding: '6px 8px',
      border: '1px solid #e2e8f0',
      borderRadius: '4px',
      fontSize: '0.8rem',
    },
    resetButton: {
      padding: '6px 12px',
      backgroundColor: '#f3f4f6',
      color: '#4b5563',
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      fontSize: '0.8rem',
      cursor: 'pointer',
      marginTop: isMobile ? '8px' : '15px',
      width: isMobile ? '100%' : 'auto',
    },
    tableContainer: {
      background: 'white',
      borderRadius: '6px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch',
      maxHeight: '70vh',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      minWidth: isMobile ? '900px' : isTablet ? '1000px' : 'auto',
      fontSize: isMobile ? '0.7rem' : isTablet ? '0.75rem' : '0.85rem',
    },
    th: {
      background: '#f7fafc',
      padding: isMobile ? '6px 4px' : isTablet ? '8px 6px' : '10px 8px',
      textAlign: 'left',
      fontWeight: '600',
      color: '#4a5568',
      borderBottom: '1px solid #e2e8f0',
      fontSize: isMobile ? '0.65rem' : isTablet ? '0.7rem' : '0.75rem',
      whiteSpace: 'nowrap',
      position: 'sticky',
      top: 0,
      zIndex: 10,
    },
    td: {
      padding: isMobile ? '6px 4px' : isTablet ? '8px 6px' : '10px 8px',
      borderBottom: '1px solid #e2e8f0',
      fontSize: isMobile ? '0.65rem' : isTablet ? '0.7rem' : '0.75rem',
      maxWidth: isMobile ? '80px' : isTablet ? '100px' : '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    dateColumn: {
      minWidth: isMobile ? '110px' : '120px',
      maxWidth: isMobile ? '130px' : '150px',
    },
    productColumn: {
      minWidth: isMobile ? '50px' : '60px',
      maxWidth: isMobile ? '50px' : '60px',
    },
    nameColumn: {
      minWidth: isMobile ? '80px' : '100px',
      maxWidth: isMobile ? '120px' : '150px',
    },
    emailColumn: {
      minWidth: isMobile ? '90px' : '120px',
      maxWidth: isMobile ? '120px' : '180px',
    },
    phoneColumn: {
      minWidth: isMobile ? '80px' : '100px',
      maxWidth: isMobile ? '80px' : '100px',
    },
    teamColumn: {
      minWidth: isMobile ? '60px' : '80px',
      maxWidth: isMobile ? '60px' : '100px',
    },
    statusColumn: {
      minWidth: isMobile ? '60px' : '80px',
      maxWidth: isMobile ? '80px' : '100px',
    },
    row: {
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    statusBadge: {
      display: 'inline-block',
      padding: isMobile ? '0.15rem 0.3rem' : '0.2rem 0.5rem',
      borderRadius: '0.25rem',
      fontSize: isMobile ? '0.55rem' : '0.65rem',
      fontWeight: '500',
      textAlign: 'center',
      whiteSpace: 'nowrap',
      minWidth: isMobile ? '35px' : '50px',
      maxWidth: isMobile ? '60px' : '80px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    statusNew: {
      backgroundColor: '#ebf8ff',
      color: '#3182ce',
    },
    statusCompleted: {
      backgroundColor: '#c6f6d5',
      color: '#38a169',
    },
    statusCancelled: {
      backgroundColor: '#fed7d7',
      color: '#e53e3e',
    },
    productBadges: {
      display: 'flex',
      gap: '0.15rem',
      flexWrap: 'wrap',
    },
    productBadge: {
      display: 'inline-block',
      padding: isMobile ? '0.1rem 0.25rem' : '0.15rem 0.4rem',
      borderRadius: '0.2rem',
      fontSize: isMobile ? '0.55rem' : '0.6rem',
      fontWeight: '700',
    },
    pvBadge: {
      backgroundColor: '#c3dafe',
      color: '#4a6de5',
    },
    wpBadge: {
      backgroundColor: '#b2f5ea',
      color: '#3182ce',
    },
    loading: {
      textAlign: 'center',
      padding: '20px',
      color: '#718096',
      fontSize: '1rem',
    },
    error: {
      color: '#e53e3e',
      backgroundColor: '#fed7d7',
      padding: '8px',
      borderRadius: '4px',
      marginBottom: '12px',
      textAlign: 'center',
      fontSize: '0.85rem',
    },
    noData: {
      textAlign: 'center',
      padding: '20px',
      color: '#a0aec0',
      fontSize: '0.9rem',
    },
  };

  // CSS-Styles für Pagination hinzufügen
  const paginationStyles = `
    .pagination-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px;
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      margin-top: 20px;
    }

    .pagination-info {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 0.875rem;
      color: #4a5568;
    }

    .pagination-controls {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .pagination-btn {
      padding: 8px 16px;
      background: #4a6de5;
      color: white;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-size: 0.875rem;
      transition: background-color 0.2s;
    }

    .pagination-btn:hover:not(:disabled) {
      background: #3c5ce0;
    }

    .pagination-btn:disabled {
      background: #cbd5e0;
      cursor: not-allowed;
    }

    .page-numbers {
      display: flex;
      gap: 4px;
    }

    .page-number {
      padding: 8px 12px;
      background: #f7fafc;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      cursor: pointer;
      font-size: 0.875rem;
      color: #2d3748;
      transition: all 0.2s;
    }

    .page-number:hover {
      background: #edf2f7;
      border-color: #cbd5e0;
    }

    .page-number.active {
      background: #4a6de5;
      color: white;
      border-color: #4a6de5;
    }

    .leads-per-page {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .leads-per-page label {
      font-size: 0.875rem;
      color: #4a5568;
    }

    .leads-per-page select {
      padding: 6px 10px;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      font-size: 0.875rem;
      background: white;
    }

    /* RESPONSIVE PAGINATION */
    @media (max-width: 768px) {
      .pagination-container {
        flex-direction: column;
        gap: 16px;
        text-align: center;
      }

      .pagination-controls {
        order: 1;
      }

      .pagination-info {
        order: 2;
      }

      .leads-per-page {
        order: 3;
      }
    }
  `;


  // Pagination-Handler
  const handlePageChange = (newPage) => {
    fetchLeads(newPage, pagination.limit);
  };

  const handleLimitChange = (newLimit) => {
    fetchLeads(1, newLimit);
  };

  // Leads abrufen mit Pagination und Filtern
  const fetchLeads = useCallback(async (page = 1, limit = 50) => {
    setLoading(true);
    setError('');
    try {
      // Filter-Parameter für Backend vorbereiten
      let filterParams = {};
      
      // Namenssuche (serverseitig)
      if (filters.search && filters.search.trim() !== '') {
        filterParams.search = filters.search.trim();
      }
      
      // Status-Filter
      if (filters.statusPv) {
        filterParams.status_pv = filters.statusPv;
      }
      if (filters.statusWp) {
        filterParams.status_wp = filters.statusWp;
      }
      
      // Team/Projekt-Filter
      if (filters.projekt) {
        filterParams.projekt = filters.projekt;
      }
      if (filters.agenturId) {
        filterParams.agentur_name = filters.agenturId;
      }
      
      // Dienstleister-Filter
      if (filters.dienstleister) {
        filterParams.dienstleister = filters.dienstleister;
      }
      
      // Datum-Filter
      if (filters.dateFrom) {
        filterParams.dateFrom = filters.dateFrom;
      }
      if (filters.dateTo) {
        filterParams.dateTo = filters.dateTo;
      }
      
      // Sortier-Parameter
      filterParams.sortBy = filters.sortBy;
      filterParams.sortOrder = filters.sortOrder;
      
      const data = await getOrganizationLeadsPaginated(user.id, currentOrganizationId, limit, page, filterParams);
      if (data.success) {
        setLeads(data.leads);
        if (data.pagination) {
          setPagination(data.pagination);
        }
        
        // Teams, Projekte, Agenturen und Dienstleister aus den geladenen Leads extrahieren
        const teams = [...new Set(data.leads.map(lead => lead.team_name).filter(Boolean))];
        const projects = [...new Set(data.leads.map(lead => lead.projekt).filter(Boolean))];
        const serviceProviders = [...new Set(data.leads.map(lead => lead.dienstleister).filter(Boolean))];
        const agenturen = [...new Set(data.leads.map(lead => lead.agentur_name).filter(Boolean))];
        
        // Verfügbare Optionen nur beim ersten Laden (ohne Filter) oder wenn noch nicht geladen
        // oder wenn neue Optionen gefunden werden, die noch nicht vorhanden sind
        if (!optionsLoaded || (!filters.search && !filters.projekt && !filters.agenturId && !filters.dienstleister)) {
          setAvailableTeams(prev => {
            const combined = [...new Set([...prev, ...teams])];
            return combined.length > 0 ? combined : prev;
          });
          setAvailableProjects(prev => {
            const combined = [...new Set([...prev, ...projects])];
            return combined.length > 0 ? combined : prev;
          });
          setAvailableServiceProviders(prev => {
            const combined = [...new Set([...prev, ...serviceProviders])];
            return combined.length > 0 ? combined : prev;
          });
          setAvailableAgenturen(prev => {
            const combined = [...new Set([...prev, ...agenturen])];
            return combined.length > 0 ? combined : prev;
          });
          setOptionsLoaded(true);
        } else {
          // Wenn Filter aktiv sind, Optionen erweitern (nicht ersetzen), damit Dropdowns nicht leer werden
          setAvailableProjects(prev => {
            const combined = [...new Set([...prev, ...projects])];
            return combined.length > 0 ? combined : prev;
          });
          setAvailableAgenturen(prev => {
            const combined = [...new Set([...prev, ...agenturen])];
            return combined.length > 0 ? combined : prev;
          });
        }
      } else {
        setError(data.error || 'Fehler beim Abrufen der Leads');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [user.id, currentOrganizationId, filters]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Leads neu laden wenn sich Filter ändern
  useEffect(() => {
    fetchLeads(1, pagination.limit);
  }, [filters]);

  // Status-Badge-Styles
  const getStatusColor = (status) => {
    if (!status) return styles.statusNew;
    status = status.toLowerCase();
    if (status.includes('offen') || status.includes('neu')) return styles.statusNew;
    if (status.includes('bearbeitung') || status.includes('in arbeit')) return { backgroundColor: '#fefcbf', color: '#d69e2e' };
    if (status.includes('abgeschlossen') || status.includes('fertig')) return styles.statusCompleted;
    if (status.includes('qualifiziert')) return styles.statusCompleted;
    if (status.includes('abgelehnt') || status.includes('storniert') || status.includes('kein interesse') || status.includes('widerruf') || status.includes('ungültig') || status.includes('test')) return styles.statusCancelled;
    return styles.statusNew;
  };

  // Status-Rendering
  const renderStatus = (status, productType, leadProduct) => {
    const hasProduct = leadProduct && leadProduct.toLowerCase().includes(productType.toLowerCase());
    if (!hasProduct) {
      return <span>-</span>;
    }
    if (!status || status.trim() === '') {
      return (
        <span style={{...styles.statusBadge, ...styles.statusNew}}>
          Offen
        </span>
      );
    }
    return (
      <span style={{...styles.statusBadge, ...getStatusColor(status)}}>
        {status}
      </span>
    );
  };

  // Filter-Handler
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  // Filter zurücksetzen
  const resetFilters = () => {
    setFilters({
      search: '',
      projekt: '',
      agenturId: '',
      sortBy: 'erstellt',
      sortOrder: 'desc',
      dateFrom: '',
      dateTo: '',
      statusPv: '',
      statusWp: '',
      dienstleister: ''
    });
  };

  // Kunde auswählen
  const handleCustomerSelect = (id) => {
    if (currentOrganizationId) {
      navigate(`/organization-leads/${currentOrganizationId}/${id}`);
    } else {
      navigate(`/organization-leads/${id}`);
    }
  };

  // Zurück zur Liste
  const handleBackToList = () => {
    if (currentOrganizationId) {
      navigate(`/organization-leads/${currentOrganizationId}`);
    } else {
      navigate('/organization-leads');
    }
  };

  // Handler für Section-Wechsel (wie in Leads.js)
  const handleSectionChange = (section) => {
    if (section === 'dashboard') {
      window.location.href = '/dashboard';
    } else if (section === 'leads') {
      window.location.href = '/leads-normal';
    } else if (section === 'stellungnahmen') {
      window.location.href = '/stellungnahmen';
    } else if (section === 'termine') {
      window.location.href = '/termine';
    } else if (section === 'organization-leads') {
      // Bleibe auf dieser Seite
    }
  };

  // Detailansicht
  if (customerId) {
    return (
      <CustomerDetail 
        user={user} 
        customerId={customerId} 
        onBack={handleBackToList} 
      />
    );
  }

  console.log(user);
  console.log(" Alle leads", leads);

  // Listenansicht mit Navigation
  return (
    <>
      <DashboardNav
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        user={user}
        showOrgManagement={false}
        showMembersManagement={false}
        showOrganizationLeads={true}
      />
      <div style={styles.container}>
        <div style={styles.header}>
          <h2 style={styles.title}>
            Organisations-Leads
            <span style={{ fontSize: '0.7em', color: '#4a6de5', marginLeft: '8px', fontWeight: 'normal' }}>
              ({leads.length})
            </span>
          </h2>
        </div>
        <LeadsFilter
          filters={filters}
          onFilterChange={handleFilterChange}
          onResetFilters={resetFilters}
          availableTeams={availableTeams}
          availableProjects={availableProjects}
          availableAgenturen={availableAgenturen}
          availableServiceProviders={availableServiceProviders}
          showSearch={true}
          showProjectFilter={true}
          showTeamFilter={false}
          showAgenturFilter={true}
          showDienstleisterFilter={true}
          showDateFilters={true}
          showStatusFilters={true}
          showSortFilters={true}
          isMobile={isMobile}
          isTablet={isTablet}
        />
        {error && <div style={styles.error}>{error}</div>}
        
        {/* Pagination Controls - Über der Tabelle */}
        {!loading && leads.length > 0 && (
          <div className="pagination-container">
            <div className="pagination-info">
              <span>Seite {pagination.currentPage} von {pagination.totalPages}</span>
              <span>•</span>
              <span>{pagination.totalCount} Leads insgesamt</span>
            </div>
            
            <div className="pagination-controls">
              <button 
                className="pagination-btn" 
                onClick={() => handlePageChange(pagination.currentPage - 1)}
                disabled={!pagination.hasPrevPage}
              >
                ← Zurück
              </button>
              
              <div className="page-numbers">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.currentPage >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = pagination.currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      className={`page-number ${pagination.currentPage === pageNum ? 'active' : ''}`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button 
                className="pagination-btn" 
                onClick={() => handlePageChange(pagination.currentPage + 1)}
                disabled={!pagination.hasNextPage}
              >
                Weiter →
              </button>
            </div>
            
            <div className="leads-per-page">
              <label>Leads pro Seite:</label>
              <select 
                value={pagination.limit} 
                onChange={(e) => handleLimitChange(parseInt(e.target.value))}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={150}>150</option>
                <option value={200}>200</option>
              </select>
            </div>
          </div>
        )}
        
        {loading ? (
          <div style={styles.loading}>Leads werden geladen...</div>
        ) : leads.length === 0 ? (
          <div style={styles.noData}>Keine Leads gefunden.</div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{...styles.th, ...styles.dateColumn}}>Erstellt</th>
                  <th style={{...styles.th, ...styles.productColumn}}>Produkt</th>
                  <th style={{...styles.th, ...styles.statusColumn}}>Opt In</th>
                  <th style={{...styles.th, ...styles.nameColumn}}>Name</th>
                  <th style={{...styles.th, ...styles.emailColumn}}>E-Mail</th>
                  <th style={{...styles.th, ...styles.phoneColumn}}>Handy</th>
                  <th style={{...styles.th, ...styles.teamColumn}}>Projekt</th>
                  <th style={{...styles.th, ...styles.statusColumn}}>Agentur</th>
                  <th style={{...styles.th, ...styles.statusColumn}}>PV Status</th>
                  <th style={{...styles.th, ...styles.statusColumn}}>WP Status</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr 
                    key={lead.id}
                    style={styles.row}
                    onClick={() => handleCustomerSelect(lead.id)}
                  >
                    <td style={{...styles.td, ...styles.dateColumn}}>
                      {new Date(lead.erstellt).toLocaleString('de-DE', {
                        dateStyle: 'short',
                        timeStyle: 'short'
                      })}
                    </td>
                    <td style={{...styles.td, ...styles.productColumn}}>
                      {lead.produkt ? (
                        <div style={styles.productBadges}>
                          {lead.produkt.toLowerCase().includes('pv') && (
                            <span style={{...styles.productBadge, ...styles.pvBadge}}>PV</span>
                          )}
                          {lead.produkt.toLowerCase().includes('wp') && (
                            <span style={{...styles.productBadge, ...styles.wpBadge}}>WP</span>
                          )}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td style={{...styles.td, ...styles.statusColumn}}>{lead.opt_in ? 'Ja' : 'Nein'}</td>
                    <td style={{...styles.td, ...styles.nameColumn}}>{`${lead.anrede ? lead.anrede + ' ' : ''}${lead.name || ''} ${lead.nachname || ''}`.trim() || '-'}</td>
                    <td style={{...styles.td, ...styles.emailColumn}}>{lead.email || '-'}</td>
                    <td style={{...styles.td, ...styles.phoneColumn}}>{lead.handy || '-'}</td>
                    <td style={{...styles.td, ...styles.teamColumn}}>{lead.team_name || lead.teamName || '-'}</td>
                    <td style={{...styles.td, ...styles.statusColumn}}>{lead.agentur_name || 'Keine Agentur'}</td>
                    <td style={{...styles.td, ...styles.statusColumn}}>{renderStatus(lead.status_pv, 'pv', lead.produkt)}</td>
                    <td style={{...styles.td, ...styles.statusColumn}}>{renderStatus(lead.status_wp, 'wp', lead.produkt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Styles */}
      <style jsx>{paginationStyles}</style>
    </>
  );
}

export default OrganizationLeads;